import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Box,
  Divider,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Icon,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  IconButton,
  Tooltip,
  useToast,
  Spinner
} from '@chakra-ui/react';
import {
  MdPerson,
  MdPhone,
  MdEmail,
  MdLocationCity,
  MdAssignment,
  MdAttachMoney,
  MdCalendarToday,
  MdComment,
  MdCheckCircle,
  MdCancel,
  MdHistory,
  MdDescription,
  MdVisibility,
  MdDownload,
  MdUploadFile
} from 'react-icons/md';
import { FaUser, FaBuilding, FaBalanceScale } from 'react-icons/fa';
import documentService from '../../../../services/documentService';
import DocumentViewer from '../../../../components/DocumentViewer';

const PrecatorioInspector = ({ isOpen, onClose, precatorio }) => {
  const [viewDocument, setViewDocument] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(null);
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const sectionBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDocument = (doc, type) => {
    if (!doc) return '-';
    const cleaned = doc.replace(/\D/g, '');
    
    if (type === 'CPF' && cleaned.length === 11) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (type === 'CNPJ' && cleaned.length === 14) {
      return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return doc;
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return phone;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'novo': 'Novo',
      'solicitacao_proposta': 'Solicitação de Proposta',
      'proposta_aceita': 'Proposta Aceita',
      'proposta_negada': 'Proposta Negada',
      'solicitacao_documentos': 'Solicitação de Documentos',
      'documentos_completos': 'Documentos Completos',
      'em_analise': 'Em Análise',
      'fechado': 'Fechado'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'novo': 'blue',
      'solicitacao_proposta': 'purple',
      'proposta_aceita': 'green',
      'proposta_negada': 'red',
      'solicitacao_documentos': 'orange',
      'documentos_completos': 'yellow',
      'em_analise': 'cyan',
      'fechado': 'green'
    };
    return colors[status] || 'gray';
  };

  const getDocumentIcon = (tipo) => {
    if (tipo === 'CPF') return FaUser;
    if (tipo === 'CNPJ') return FaBuilding;
    if (tipo === 'OAB') return FaBalanceScale;
    return null;
  };

  const getChecklistLabel = (key) => {
    const labels = {
      'rg_cpf_cnh': 'RG, CPF ou CNH',
      'comprovante_residencia': 'Comprovante de Residência',
      'certidao_casamento': 'Certidão de Casamento',
      'dados_bancarios': 'Dados Bancários',
      'contrato_honorarios': 'Contrato de Honorários',
      'selfie_documento': 'Selfie com Documento',
      'certidao_estadual': 'Certidão Estadual',
      'certidao_federal': 'Certidão Federal',
      'contrato_social': 'Contrato Social',
      'certidao_inteiro_teor': 'Certidão de Inteiro Teor',
      'rg_cpf_socios': 'RG/CPF dos Sócios',
      'comprovante_endereco_empresa': 'Comprovante de Endereço da Empresa',
      'dados_bancarios_empresa': 'Dados Bancários da Empresa'
    };
    return labels[key] || key;
  };

  const handleViewDocument = async (docType) => {
    try {
      setLoadingDoc(docType);
      const response = await documentService.getDocumentUrl(
        precatorio.state,
        precatorio._id,
        docType
      );

      if (response.success && response.data) {
        setViewDocument({
          url: response.data.url,
          fileName: response.data.fileName,
          fileType: response.data.fileType
        });
      }
    } catch (error) {
      toast({
        title: 'Erro ao visualizar documento',
        description: error.message || 'Não foi possível abrir o documento',
        status: 'error',
        duration: 5000
      });
    } finally {
      setLoadingDoc(null);
    }
  };

  const handleDownloadDocument = async (docType) => {
    try {
      setLoadingDoc(docType);
      const doc = precatorio.documents?.[docType];
      await documentService.downloadDocument(
        precatorio.state,
        precatorio._id,
        docType,
        doc?.fileName || `documento_${docType}`
      );

      toast({
        title: 'Download iniciado',
        description: 'O arquivo está sendo baixado.',
        status: 'info',
        duration: 3000
      });
    } catch (error) {
      toast({
        title: 'Erro ao baixar documento',
        description: error.message || 'Não foi possível baixar o arquivo',
        status: 'error',
        duration: 5000
      });
    } finally {
      setLoadingDoc(null);
    }
  };

  const getRequiredDocuments = () => {
    if (!precatorio?.document_type_used) return {};

    const cpfOabDocuments = {
      'rg_cpf_cnh': 'RG, CPF ou CNH',
      'comprovante_residencia': 'Comprovante de Residência',
      'certidao_casamento': 'Certidão de Casamento (se aplicável)',
      'dados_bancarios': 'Dados Bancários',
      'contrato_honorarios': 'Contrato de Honorários',
      'selfie_documento': 'Selfie com Documento',
      'certidao_estadual': 'Certidão Estadual',
      'certidao_federal': 'Certidão Federal',
      'certidao_trabalhista': 'Certidão Trabalhista (opcional)',
      'certidao_civel': 'Certidão Cível (opcional)',
      'certidao_insolvencia': 'Certidão Insolvência (opcional)'
    };

    const cnpjDocuments = {
      'contrato_social': 'Contrato Social',
      'certidao_inteiro_teor': 'Certidão de Inteiro Teor',
      'rg_cpf_socios': 'RG/CPF dos Sócios',
      'comprovante_endereco_empresa': 'Comprovante de Endereço da Empresa',
      'dados_bancarios_empresa': 'Dados Bancários da Empresa',
      'certidao_trabalhista': 'Certidão Trabalhista (opcional)',
      'certidao_civel': 'Certidão Cível (opcional)',
      'certidao_insolvencia': 'Certidão Insolvência (opcional)'
    };

    return precatorio.document_type_used === 'CNPJ' ? cnpjDocuments : cpfOabDocuments;
  };

  if (!precatorio) return null;

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" scrollBehavior="inside" isCentered>
      <ModalOverlay />
      <ModalContent maxH="90vh" overflow="hidden">
        <ModalHeader>
          <HStack spacing={4}>
            <Icon as={MdAssignment} boxSize={6} />
            <VStack align="start" spacing={0}>
              <Text>Precatório #{precatorio.numero_precatorio}</Text>
              <Badge colorScheme={getStatusColor(precatorio.status)}>
                {getStatusLabel(precatorio.status)}
              </Badge>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody p={0}>
          <Tabs display="flex" flexDirection="column" h="calc(90vh - 180px)">
            <TabList px={6} pt={4}>
              <Tab>Informações Gerais</Tab>
              <Tab>Contatos</Tab>
              <Tab>Documentos</Tab>
              <Tab>Histórico</Tab>
            </TabList>

            <TabPanels overflowY="auto" flex="1" px={6}>
              {/* General Information Tab */}
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  {/* Basic Info */}
                  <Box bg={sectionBg} p={4} borderRadius="md">
                    <Text fontWeight="bold" mb={3}>Dados Básicos</Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <HStack align="start">
                        <Icon as={MdPerson} color="blue.500" />
                        <Box>
                          <Text fontSize="sm" color={textColor}>Nome</Text>
                          <Text fontWeight="medium">{precatorio.nome}</Text>
                        </Box>
                      </HStack>

                      <HStack align="start">
                        <Icon as={getDocumentIcon(precatorio.document_type_used)} color="blue.500" />
                        <Box>
                          <Text fontSize="sm" color={textColor}>Documento Usado</Text>
                          <Text fontWeight="medium">
                            {formatDocument(precatorio.document_used, precatorio.document_type_used)}
                          </Text>
                          <Badge size="sm">{precatorio.document_type_used}</Badge>
                        </Box>
                      </HStack>

                      {precatorio.cpf_cnpj && (
                        <HStack align="start">
                          <Icon as={getDocumentIcon(precatorio.tipo_documento)} color="gray.500" />
                          <Box>
                            <Text fontSize="sm" color={textColor}>CPF/CNPJ</Text>
                            <Text fontWeight="medium">
                              {formatDocument(precatorio.cpf_cnpj, precatorio.tipo_documento)}
                            </Text>
                          </Box>
                        </HStack>
                      )}

                      <HStack align="start">
                        <Icon as={MdLocationCity} color="blue.500" />
                        <Box>
                          <Text fontSize="sm" color={textColor}>Localização</Text>
                          <Text fontWeight="medium">
                            {precatorio.cidade || '-'} - {precatorio.uf || precatorio.state}
                          </Text>
                        </Box>
                      </HStack>
                    </SimpleGrid>
                  </Box>

                  {/* Financial Info */}
                  <Box bg={sectionBg} p={4} borderRadius="md">
                    <Text fontWeight="bold" mb={3}>Informações Financeiras</Text>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                      <HStack align="start">
                        <Icon as={MdAttachMoney} color="green.500" />
                        <Box>
                          <Text fontSize="sm" color={textColor}>Valor Original</Text>
                          <Text fontWeight="medium">{formatCurrency(precatorio.valor_original)}</Text>
                        </Box>
                      </HStack>

                      <HStack align="start">
                        <Icon as={MdAttachMoney} color="green.500" />
                        <Box>
                          <Text fontSize="sm" color={textColor}>Valor Atualizado</Text>
                          <Text fontWeight="medium">{formatCurrency(precatorio.valor_atualizado)}</Text>
                        </Box>
                      </HStack>

                      {precatorio.valor_proposta && (
                        <HStack align="start">
                          <Icon as={MdAttachMoney} color="purple.500" />
                          <Box>
                            <Text fontSize="sm" color={textColor}>Valor da Proposta</Text>
                            <Text fontWeight="medium">{formatCurrency(precatorio.valor_proposta)}</Text>
                          </Box>
                        </HStack>
                      )}
                    </SimpleGrid>
                  </Box>

                  {/* Additional Info */}
                  <Box bg={sectionBg} p={4} borderRadius="md">
                    <Text fontWeight="bold" mb={3}>Informações Adicionais</Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <HStack align="start">
                        <Icon as={MdDescription} color="blue.500" />
                        <Box>
                          <Text fontSize="sm" color={textColor}>Natureza</Text>
                          <Text fontWeight="medium">{precatorio.natureza || '-'}</Text>
                        </Box>
                      </HStack>

                      <HStack align="start">
                        <Icon as={MdCalendarToday} color="blue.500" />
                        <Box>
                          <Text fontSize="sm" color={textColor}>Ano Pagamento Previsto</Text>
                          <Text fontWeight="medium">{precatorio.ano_pagamento_previsto || '-'}</Text>
                        </Box>
                      </HStack>

                      <HStack align="start">
                        <Icon as={MdCheckCircle} color="blue.500" />
                        <Box>
                          <Text fontSize="sm" color={textColor}>Prioridade</Text>
                          <Badge colorScheme={precatorio.prioridade === 'Sim' ? 'green' : 'gray'}>
                            {precatorio.prioridade || 'Não'}
                          </Badge>
                        </Box>
                      </HStack>

                      <HStack align="start">
                        <Icon as={MdPerson} color="blue.500" />
                        <Box>
                          <Text fontSize="sm" color={textColor}>Responsável</Text>
                          <Text fontWeight="medium">
                            {precatorio.responsavel_atual?.name || '-'}
                          </Text>
                        </Box>
                      </HStack>
                    </SimpleGrid>
                  </Box>

                  {/* OAB Info */}
                  {precatorio.oab_info && precatorio.oab_info.length > 0 && (
                    <Box bg={sectionBg} p={4} borderRadius="md">
                      <Text fontWeight="bold" mb={3}>Informações OAB</Text>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                        {precatorio.oab_info.map((oab, index) => (
                          <HStack key={index} align="start">
                            <Icon as={FaBalanceScale} color="blue.500" />
                            <Box>
                              <Text fontWeight="medium">{oab.nome}</Text>
                              <Text fontSize="sm" color={textColor}>
                                {oab.numero}/{oab.uf}
                              </Text>
                            </Box>
                          </HStack>
                        ))}
                      </SimpleGrid>
                    </Box>
                  )}
                </VStack>
              </TabPanel>

              {/* Contacts Tab */}
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  {/* Phones */}
                  {precatorio.telefones_contato && precatorio.telefones_contato.length > 0 && (
                    <Box bg={sectionBg} p={4} borderRadius="md">
                      <Text fontWeight="bold" mb={3}>Telefones</Text>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                        {precatorio.telefones_contato.map((phone, index) => (
                          <HStack key={index}>
                            <Icon as={MdPhone} color="green.500" />
                            <Text>{formatPhone(phone)}</Text>
                          </HStack>
                        ))}
                      </SimpleGrid>
                    </Box>
                  )}

                  {/* Emails */}
                  {precatorio.emails_contato && precatorio.emails_contato.length > 0 && (
                    <Box bg={sectionBg} p={4} borderRadius="md">
                      <Text fontWeight="bold" mb={3}>E-mails</Text>
                      <VStack align="start" spacing={2}>
                        {precatorio.emails_contato.map((email, index) => (
                          <HStack key={index}>
                            <Icon as={MdEmail} color="blue.500" />
                            <Text>{email}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  )}

                  {(!precatorio.telefones_contato?.length && !precatorio.emails_contato?.length) && (
                    <Box textAlign="center" py={10}>
                      <Text color="gray.500">Nenhum contato disponível</Text>
                    </Box>
                  )}
                </VStack>
              </TabPanel>

              {/* Documents Tab */}
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  {precatorio.checklist && (
                    <Box bg={sectionBg} p={4} borderRadius="md">
                      <HStack justify="space-between" mb={3}>
                        <Text fontWeight="bold">Checklist de Documentos</Text>
                        <Badge colorScheme={precatorio.checklist_completed ? 'green' : 'orange'}>
                          {precatorio.checklist_completed ? 'Completo' : 'Incompleto'}
                        </Badge>
                      </HStack>

                      <VStack align="stretch" spacing={3}>
                        {Object.entries(getRequiredDocuments()).map(([key, label]) => {
                          const doc = precatorio.documents?.[key];
                          const isUploaded = doc?.uploaded || false;
                          const isChecked = precatorio.checklist?.[key] || false;

                          return (
                            <Box
                              key={key}
                              p={3}
                              borderWidth="1px"
                              borderRadius="md"
                              bg={isUploaded ? "green.50" : "gray.50"}
                              borderColor={isUploaded ? "green.300" : "gray.200"}
                            >
                              <HStack justify="space-between">
                                <HStack flex={1}>
                                  {isUploaded ? (
                                    <Icon as={MdCheckCircle} color="green.500" boxSize={5} />
                                  ) : (
                                    <Icon as={MdCancel} color="red.500" boxSize={5} />
                                  )}
                                  <VStack align="start" spacing={0}>
                                    <Text fontWeight={isUploaded ? "bold" : "normal"}>
                                      {label}
                                    </Text>
                                    {isUploaded && doc ? (
                                      <Text fontSize="xs" color="green.600">
                                        {doc.fileName} • {documentService.formatFileSize(doc.fileSize)}
                                      </Text>
                                    ) : (
                                      <Text fontSize="xs" color="gray.500">
                                        Documento pendente
                                      </Text>
                                    )}
                                  </VStack>
                                  {isUploaded && (
                                    <Badge colorScheme="green" ml={2}>
                                      <MdUploadFile style={{ display: 'inline', marginRight: '4px' }} />
                                      Enviado
                                    </Badge>
                                  )}
                                </HStack>

                                <HStack>
                                  {isUploaded && (
                                    <>
                                      <Tooltip label="Visualizar">
                                        <IconButton
                                          size="sm"
                                          icon={loadingDoc === `view-${key}` ? <Spinner size="sm" /> : <MdVisibility />}
                                          onClick={() => handleViewDocument(key)}
                                          aria-label="Visualizar documento"
                                          isDisabled={loadingDoc === `view-${key}`}
                                        />
                                      </Tooltip>
                                      <Tooltip label="Baixar">
                                        <IconButton
                                          size="sm"
                                          icon={loadingDoc === `download-${key}` ? <Spinner size="sm" /> : <MdDownload />}
                                          onClick={() => handleDownloadDocument(key)}
                                          aria-label="Baixar documento"
                                          isDisabled={loadingDoc === `download-${key}`}
                                        />
                                      </Tooltip>
                                    </>
                                  )}
                                </HStack>
                              </HStack>
                            </Box>
                          );
                        })}
                      </VStack>
                    </Box>
                  )}

                  {!precatorio.checklist && (
                    <Box textAlign="center" py={10}>
                      <Text color="gray.500">Nenhuma informação de documentos disponível</Text>
                    </Box>
                  )}
                </VStack>
              </TabPanel>

              {/* History Tab */}
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  {/* Timeline */}
                  <Box bg={sectionBg} p={4} borderRadius="md">
                    <Text fontWeight="bold" mb={3}>Linha do Tempo</Text>
                    <VStack align="start" spacing={3}>
                      <HStack>
                        <Icon as={MdHistory} color="blue.500" />
                        <Box>
                          <Text fontSize="sm" color={textColor}>Primeiro Contato</Text>
                          <Text fontWeight="medium">{formatDate(precatorio.data_primeiro_contato)}</Text>
                          {precatorio.primeiro_contato_por && (
                            <Text fontSize="xs" color={textColor}>
                              Por: {precatorio.primeiro_contato_por.name}
                            </Text>
                          )}
                        </Box>
                      </HStack>

                      <HStack>
                        <Icon as={MdHistory} color="green.500" />
                        <Box>
                          <Text fontSize="sm" color={textColor}>Última Atualização</Text>
                          <Text fontWeight="medium">{formatDate(precatorio.data_ultima_atualizacao)}</Text>
                        </Box>
                      </HStack>

                      {precatorio.data_proximo_contato && (
                        <HStack>
                          <Icon as={MdCalendarToday} color="orange.500" />
                          <Box>
                            <Text fontSize="sm" color={textColor}>Próximo Contato</Text>
                            <Text fontWeight="medium">{formatDate(precatorio.data_proximo_contato)}</Text>
                          </Box>
                        </HStack>
                      )}
                    </VStack>
                  </Box>

                  {/* Comments */}
                  {precatorio.comentarios && precatorio.comentarios.length > 0 && (
                    <Box bg={sectionBg} p={4} borderRadius="md">
                      <Text fontWeight="bold" mb={3}>Comentários ({precatorio.comentarios.length})</Text>
                      <VStack align="stretch" spacing={3}>
                        {precatorio.comentarios.map((comment, index) => (
                          <Box key={index} p={3} bg={bgColor} borderRadius="md" borderWidth="1px">
                            <HStack justify="space-between" mb={2}>
                              <HStack>
                                <Icon as={MdComment} color="blue.500" />
                                <Text fontWeight="medium" fontSize="sm">
                                  {comment.usuario?.name || 'Usuário'}
                                </Text>
                              </HStack>
                              <Text fontSize="xs" color={textColor}>
                                {formatDate(comment.data)}
                              </Text>
                            </HStack>
                            <Text fontSize="sm">{comment.texto}</Text>
                            {comment.tipo && (
                              <Badge mt={2} size="sm" colorScheme="blue">
                                {comment.tipo}
                              </Badge>
                            )}
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  )}

                  {(!precatorio.comentarios || precatorio.comentarios.length === 0) && (
                    <Box textAlign="center" py={10}>
                      <Text color="gray.500">Nenhum comentário registrado</Text>
                    </Box>
                  )}
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose}>Fechar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    {/* Document Viewer Modal */}
    {viewDocument && (
      <DocumentViewer
        isOpen={!!viewDocument}
        onClose={() => setViewDocument(null)}
        documentUrl={viewDocument.url}
        fileName={viewDocument.fileName}
        fileType={viewDocument.fileType}
      />
    )}
    </>
  );
};

export default PrecatorioInspector;